import './styles/index.scss';

import * as RoutesModule from 'Routes';

import { AsyncStorage, PersistedQuery, experimental_createQueryPersister } from '@tanstack/react-query-persist-client';
import { BearerToken, BearerTokenProfileInfo } from 'types/StartupTypes';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router';
import { StrictMode, createContext, useContext } from 'react';
import { UseStore, createStore, del, get, set } from 'idb-keyval';

import { AuthenticationService } from 'services/AuthenticationService';
import { AxiosServiceConfig } from 'services/AxiosServiceConfig';
import { AxiosServiceFactory } from 'services/AxiosServiceFactory';
import CodegenDashboardService from 'services/CodegenDashboardService';
import CodegenLookupService from 'services/CodegenLookupService';
import CodegenReportService from 'services/CodegenReportService';
import { ConfigurationService } from 'services/ConfigurationService';
import Hotjar from '@hotjar/browser';
import StartupService from 'services/StartupService';
import { createRoot } from 'react-dom/client';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import translationsEn from './locales/en/translation-en.json';
import { useApplicationUserRightsStore } from 'stores/ApplicationUserRightsStore';
import { useNotificationsStore } from 'stores/NotificationsStore';
import { AppPermissions } from 'constants/AppPermissions';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { appInsights, reactPlugin } from 'utilities/telemetry';
import ExternalReportService from 'services/ExternalReportService';

const rootElement = document.getElementById('root');
const root = createRoot(rootElement!);

if (process.env.NODE_ENV === 'production') {
  const siteId = 5247736;
  const hotjarVersion = 6;
  Hotjar.init(siteId, hotjarVersion);
}

type Stores = {
  me: BearerTokenProfileInfo;
  startupService: StartupService;
  codegenDashboardService: CodegenDashboardService;
  codegenLookupService: CodegenLookupService;
  codegenReportService: CodegenReportService;
  externalReportService: ExternalReportService;
};

const StoresContext = createContext<Stores | null>(null);

export const useStores = () => {
  const stores = useContext(StoresContext);
  if (!stores) {
    throw new Error('useStores must be used within a StoreProvider.');
  }
  return stores;
};

const langResources = {
  en: { translation: translationsEn },
};

export const isLangAvailable = (lang: string) => {
  return Object.keys(langResources).includes(lang) ? lang : '';
};

const newIdbStorage = (idbStore: UseStore): AsyncStorage<PersistedQuery> => {
  return {
    getItem: async (key) => await get(key, idbStore),
    setItem: async (key, value) => await set(key, value, idbStore),
    removeItem: async (key) => await del(key, idbStore),
  };
};

export const persister = experimental_createQueryPersister({
  storage: newIdbStorage(createStore('infiniteQueries', 'infiniteQueries')),
  maxAge: Infinity,
  buster: 'persist-v1', // manually change this value to invalidate all locally cached results, if you want to invalidate all cache on every build put String(Date.now()) here
});

async function RenderApp() {
  const authenticationService = new AuthenticationService();
  const configurationService = new ConfigurationService();

  // Without the token and config this app doesn't work, so getting them blocks the main rendering thread intentionally – spinner is shown in index.html
  const [config] = await Promise.all([configurationService.getConfigs(), authenticationService.initialize()]);
  const authData = authenticationService.getAuthData();

  const decodedToken = authData ? jwtDecode<BearerToken>(authData.accessToken) : {};
  const permissionsArray = decodedToken.permission
    ? Array.isArray(decodedToken.permission)
      ? decodedToken.permission
      : [decodedToken.permission]
    : [];
  const me: BearerTokenProfileInfo = {
    permissions: permissionsArray,
    name: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'] ?? '',
    surname: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'] ?? '',
    userId: decodedToken.sub ?? '',
    identityUrl: decodedToken.iss ?? '',
    preferredLanguage: decodedToken['preferred-language'] ?? '',
    preferredUnitSystem: decodedToken['preferred-unit-system'] ?? '',
    sport: decodedToken.sport ?? '',
    organizationId: decodedToken.organizationid ?? '',
    playerId: decodedToken.playerid ?? '',
    umpireId: decodedToken.umpireid ?? '',
    organizationName: decodedToken.organizationname ?? '',
    hasBearerToken: !!authData,
  };

  if (!me.organizationId && window.location.pathname !== RoutesModule.DefaultRoutes.MissingOrganization) {
    window.history.replaceState({}, '', RoutesModule.DefaultRoutes.MissingOrganization);
  }

  useApplicationUserRightsStore
    .getState()
    .setData({ permissions: me.permissions, isAdmin: decodedToken.security_group?.includes(AppPermissions.Admin) ?? false });

  const serviceFactory = new AxiosServiceFactory(configurationService);
  const internalApiServiceAxiosInstance = await serviceFactory.createInstance('internal');
  const externalApiServiceAxiosInstance = await serviceFactory.createInstance('external');
  const startupService = new StartupService(internalApiServiceAxiosInstance, configurationService);
  const externalReportService = new ExternalReportService(internalApiServiceAxiosInstance);

  const codegenDashboardService = new CodegenDashboardService(externalApiServiceAxiosInstance);
  const codegenLookupService = new CodegenLookupService(externalApiServiceAxiosInstance);
  const codegenReportService = new CodegenReportService(externalApiServiceAxiosInstance);

  const stores: Stores = {
    me,
    startupService,
    codegenDashboardService,
    codegenLookupService,
    codegenReportService,
    externalReportService,
  };

  await i18next.use(initReactI18next).init({
    resources: langResources,
    lng: isLangAvailable(me.preferredLanguage) || isLangAvailable(navigator.language.slice(0, 2)) || 'en',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
  });

  // Add a request and response interceptor
  AxiosServiceConfig.setDefaultDecorators(internalApiServiceAxiosInstance, authenticationService);
  AxiosServiceConfig.setDefaultDecorators(externalApiServiceAxiosInstance, authenticationService);

  const router = createBrowserRouter(createRoutesFromElements(RoutesModule.routes));

  const { addToast } = useNotificationsStore.getState();

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        gcTime: 1000,
        staleTime: 0, // data is persisted forever, but updated in the background on every page load
        persister: persister.persisterFn,
      },
    },
    queryCache: new QueryCache({
      onError: (error) => {
        addToast(i18next.t('Error'), i18next.t(error.message), 'error');
      },
      onSuccess(data, query) {
        if (typeof query.meta?.successMessage === 'string' && typeof query.meta?.successHeader === 'string') {
          let shouldAddToast = true;
          // add if statements here to check if the toast should be added, example below
          // if (query.meta.isOnPageWithoutToasts || Array.isArray(data)) shouldAddToast = false;
          if (shouldAddToast) addToast(query.meta.successHeader, query.meta.successMessage, 'success');
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        addToast(i18next.t('Error'), error.message, 'error');
      },
    }),
  });

  root.render(
    <StrictMode>
      <AppInsightsWrapper>
        <QueryClientProvider client={queryClient}>
          <StoresContext.Provider value={stores}>
            <RouterProvider router={router} />
          </StoresContext.Provider>
        </QueryClientProvider>
      </AppInsightsWrapper>
    </StrictMode>
  );
}

RenderApp();

const AppInsightsWrapper = ({ children }: { children: React.ReactNode }) => {
  if (!appInsights) {
    return <>{children}</>;
  }

  return <AppInsightsContext.Provider value={reactPlugin}>{children}</AppInsightsContext.Provider>;
};
